import {
  Autocomplete,
  Box,
  Chip,
  FormControlProps,
  FormHelperText,
  TextField,
} from '@mui/material';
import { debounce, get } from 'lodash';
import { bool, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  CommonInputProps,
  InputHelperText,
  Labeled,
  RaRecord,
  useGetList,
  useInput,
  useTranslate,
} from 'react-admin';
import { useFieldArray } from 'react-hook-form';
import { match } from 'ts-pattern';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';

import { Pagination } from '@sh4res/bo-common/components';
import { TranslationKeys } from '@sh4res/bo-common/i18n/types';
import { inputDisabledProps, RaSort, Resources } from '@sh4res/bo-common/utils';

// A filter entry only counts as "present" when it carries a value worth querying on:
// nullish, empty-string and empty-array entries are ignored.
const isNonEmptyFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

interface AddIdInputProps<Query extends BoListQueryBase, R extends RaRecord> {
  isRequired: boolean;
  label?: TranslationKeys;
  reference: Resources;
  filter?: Query['filter'];
  target: keyof Exclude<Query['filter'], undefined>;
  sort: RaSort<Query>;
  getLabel: (record: R) => string;
  limit: number;
  disabled: boolean;
  onChange: (record: R) => unknown;
  fetchChoicesOnMountIfFilterPresent?: boolean;
  displayPrefetchedOptionsWithoutSearchText?: boolean;
  errorId?: string;
  hasError?: boolean;
  'data-testid'?: string;
}

function AddIdInput<Q extends BoListQueryBase, R extends RaRecord>({
  isRequired,
  label,
  limit,
  filter,
  target,
  reference,
  sort,
  disabled,
  getLabel,
  onChange,
  fetchChoicesOnMountIfFilterPresent = false,
  displayPrefetchedOptionsWithoutSearchText = false,
  errorId,
  hasError,
  'data-testid': dataTestId,
}: AddIdInputProps<Q, R>) {
  const translate = useTranslate();
  const [searchText, setSearchText] = useState('');
  const [filterText, setFilterText] = useState('');

  const shouldFetchOnMount =
    fetchChoicesOnMountIfFilterPresent &&
    filter &&
    Object.keys(filter).some((key) => key !== 'id' && isNonEmptyFilterValue(filter[key]));
  const enabledCondition = !!filterText || shouldFetchOnMount;

  const { data = [], isLoading } = useGetList<R>(
    reference,
    {
      pagination: { page: 1, perPage: limit },
      filter: { ...filter, [target]: filterText },
      sort,
    },
    { enabled: enabledCondition },
  );

  const debouncedSetFilterText = useMemo(() => debounce(setFilterText, 500), []);

  useEffect(() => {
    debouncedSetFilterText(searchText);
    return () => {
      debouncedSetFilterText.cancel();
    };
  }, [searchText, debouncedSetFilterText]);

  let autocompleteOptions: R[] = [];
  if (
    !isLoading &&
    (searchText || (displayPrefetchedOptionsWithoutSearchText && data.length > 0))
  ) {
    autocompleteOptions = data;
  }

  const noOptionsText = match({
    searchTextIsSet: !!searchText,
    isLoading,
    dataIsSet: autocompleteOptions.length > 0,
    searchTextIsFilterText: searchText === filterText,
  })
    .with({ searchTextIsSet: false, dataIsSet: false }, () =>
      translate('resources.bo_common.actions.type_to_search'),
    )
    .with({ searchTextIsSet: true, dataIsSet: false, isLoading: true }, () =>
      translate('ra.page.loading'),
    )
    .with({ searchTextIsSet: true, dataIsSet: false, searchTextIsFilterText: false }, () =>
      translate('ra.page.loading'),
    )
    .otherwise(() => undefined);

  return (
    <Autocomplete<R>
      value={null}
      onChange={(event, newValue) => {
        event.preventDefault();
        if (newValue) {
          onChange(newValue);
        }
      }}
      inputValue={searchText}
      onInputChange={(event, newInputValue) => {
        if (event.type === 'click') {
          setSearchText('');
          return;
        }
        setSearchText(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ? translate(label) : null}
          required={isRequired}
          variant="filled"
          sx={{ margin: 0 }}
          error={hasError}
          aria-invalid={hasError}
          aria-describedby={errorId}
          data-testid={dataTestId}
          inputProps={{
            ...params.inputProps,
            'aria-describedby':
              hasError && errorId ? errorId : params.inputProps['aria-describedby'],
            'aria-invalid': hasError,
          }}
        />
      )}
      noOptionsText={noOptionsText}
      options={autocompleteOptions}
      filterOptions={(x) => x}
      getOptionLabel={getLabel}
      slotProps={{
        popper: {
          disablePortal: true,
        },
      }}
      {...inputDisabledProps(disabled)}
    />
  );
}

export interface IdArrayInputProps<Query extends BoListQueryBase, R extends RaRecord>
  extends CommonInputProps,
    Omit<FormControlProps, 'defaultValue' | 'onBlur' | 'onChange' | 'onFocus'> {
  searchFilter?: Query['filter'];
  searchTarget?: keyof Exclude<Query['filter'], undefined>;
  searchLimit?: number;
  addIdLabel?: TranslationKeys;
  sort?: RaSort<Query>;
  source: string;
  reference: Resources;
  disabled?: boolean;
  label?: TranslationKeys | false;
  getLabel?: (record: R) => string;
  idSource?: keyof R;
  onChangeListing?: (listing: R[]) => void;
  defaultValue?: (number | string)[];
  canRemove?: (item: R) => boolean;
  fetchChoicesOnMountIfFilterPresent?: boolean;
  displayPrefetchedOptionsWithoutSearchText?: boolean;
  'data-testid'?: string;
}

// useFieldArray keeps the array-level message on `root`. JSON and `error.message` miss it.
const getValidationMessage = (fieldError: unknown): string | undefined => {
  if (!fieldError || typeof fieldError !== 'object') {
    return undefined;
  }

  if ('message' in fieldError && typeof fieldError.message === 'string') {
    return fieldError.message;
  }

  if (
    'root' in fieldError &&
    fieldError.root &&
    typeof fieldError.root === 'object' &&
    'message' in fieldError.root &&
    typeof fieldError.root.message === 'string'
  ) {
    return fieldError.root.message;
  }

  return undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is irrelevant here
const DEFAULT_LISTING_VALUE: any[] = [];

export function IdArrayInput<Q extends BoListQueryBase, R extends RaRecord>({
  onChange,
  onBlur,
  source,
  label,
  addIdLabel,
  reference,
  validate,
  idSource = 'id',
  searchFilter = {},
  searchTarget = 'query',
  searchLimit = 10,
  sort = { field: 'id', order: 'ASC' },
  getLabel = (record: RaRecord) => String(get(record, idSource)),
  disabled = false,
  isRequired: isRequiredProp = false,
  onChangeListing,
  defaultValue = [],
  canRemove = () => true,
  fetchChoicesOnMountIfFilterPresent = false,
  displayPrefetchedOptionsWithoutSearchText = false,
  'data-testid': dataTestId,
}: IdArrayInputProps<Q, R>) {
  const fieldArray = useFieldArray({ name: source });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const {
    field,
    isRequired,
    fieldState: { error, isTouched },
    formState: { isSubmitted, errors },
  } = useInput({
    onChange,
    onBlur,
    source,
    validate,
    defaultValue,
    isRequired: isRequiredProp,
  });
  const translate = useTranslate();

  const {
    data: listing = DEFAULT_LISTING_VALUE,
    error: listingError,
    total,
  } = useGetList<R>(
    reference,
    {
      pagination: { page, perPage },
      filter: { [idSource]: field.value },
      sort,
    },
    { keepPreviousData: true },
  );

  useEffect(() => {
    if (onChangeListing) {
      onChangeListing(listing);
    }
  }, [listing, onChangeListing]);

  if (listingError) {
    return null;
  }

  const isEmpty = field.value.length === 0;
  const localErrorMessage = getValidationMessage(error);
  const hasLocalError = !!localErrorMessage;
  const isInFormErrors = Object.keys(errors).includes(source);
  // Stepper Next calls trigger() without submitting. Field-array failures are
  // stored on `root`, and a filled field must show them before submit.
  const shouldShowError =
    (hasLocalError && (isTouched || isSubmitted || isInFormErrors)) ||
    (isRequired && isEmpty && isInFormErrors);

  const errorId = `${source}-error`;
  const errorMessage = hasLocalError ? localErrorMessage : translate('ra.validation.required');

  const hasLabel = label !== false && label !== undefined;
  const Container = hasLabel ? Labeled : Box;
  // `Box` doesn't accept `label`/`fullWidth`, it forwards unknown props
  // straight to its underlying DOM element, which React then warns about.
  return (
    <Container
      {...(hasLabel ? { label, fullWidth: true } : {})}
      sx={(theme) => ({ margin: theme.spacing(1, 0) })}>
      <>
        <AddIdInput<Q, R>
          label={addIdLabel}
          isRequired={isRequired}
          reference={reference}
          filter={searchFilter}
          sort={sort}
          getLabel={getLabel}
          target={searchTarget}
          limit={searchLimit}
          // eslint-disable-next-line @sh4res/no-disabled-prop-react-admin-components -- fine for this component
          disabled={disabled}
          fetchChoicesOnMountIfFilterPresent={fetchChoicesOnMountIfFilterPresent}
          displayPrefetchedOptionsWithoutSearchText={displayPrefetchedOptionsWithoutSearchText}
          errorId={errorId}
          hasError={shouldShowError}
          data-testid={`${dataTestId}_SearchInput`}
          onChange={(record) => {
            const newId = get(record, idSource);
            if (!field.value.includes(newId)) {
              fieldArray.append(newId);
            }
          }}
        />
        <Box>
          {listing
            .sort((itemA, itemB) => {
              if (canRemove(itemA) !== canRemove(itemB)) {
                return canRemove(itemA) ? 1 : -1;
              }

              return get(itemA, idSource) - get(itemB, idSource);
            })
            .map((item) => (
              <Chip
                label={getLabel(item)}
                key={`instrument-${get(item, idSource) as string}`}
                disabled={disabled}
                onDelete={
                  canRemove(item)
                    ? () => {
                        const index = field.value.indexOf(get(item, idSource));
                        if (index >= 0) {
                          fieldArray.remove(index);
                        }
                      }
                    : undefined
                }
                sx={(theme) => ({
                  margin: theme.spacing(1, 1),
                  backgroundColor: canRemove(item) ? undefined : 'lightgrey',
                })}
              />
            ))}
        </Box>
        {total !== undefined && (page > 1 || total > perPage) ? (
          <Pagination
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={(page: number) => {
              setPage(1);
              setPerPage(page);
            }}
            total={total}
          />
        ) : null}
        {shouldShowError ? (
          <FormHelperText
            error={true}
            id={errorId}
            role="alert"
            aria-live="polite"
            sx={{ marginLeft: '14px', marginRight: '14px', marginTop: '4px' }}>
            <InputHelperText
              touched={isTouched || isSubmitted || isInFormErrors}
              error={errorMessage}
            />
          </FormHelperText>
        ) : null}
      </>
    </Container>
  );
}

IdArrayInput.propTypes = {
  searchFilter: object,
  searchTarget: string,
  searchLimit: number,
  addIdLabel: string,
  sort: shape({
    field: string.isRequired,
    order: oneOf(['ASC', 'DESC']),
  }),
  source: string.isRequired,
  reference: string.isRequired,
  disabled: bool,
  label: oneOfType([string, bool]),
  getLabel: func,
  isRequired: bool,
  idSource: string,
  fetchChoicesOnMountIfFilterPresent: bool,
  displayPrefetchedOptionsWithoutSearchText: bool,
};
